import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import { History, X } from "lucide-react";
import { Modal } from "../ui/Modal/Modal";
import { LoadingSpinner } from "../ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../ui/ErrorFallback/ErrorFallback";
import { useOrderChangeLog, type ChangeLogChange } from "../../hooks/use-orders";
import { formatDate } from "../../lib/date";
import styles from "./OrderChangeLogModal.module.css";

function CrmUser({ name, sapName }: { name: string | null; sapName: string | null }) {
	const { t } = useTranslation();
	if (!name) return <>{sapName ?? "—"}</>;
	return (
		<span className={styles.crmUser} title={t("changeLogCrmUserHint", { sapUser: sapName ?? "" })}>
			{name}
			<span className={styles.crmBadge}>CRM</span>
		</span>
	);
}

function formatSapDateTime(value: string | null): string {
	if (!value) return "—";
	return value.includes("T") ? format(parseISO(value), "dd/MM/yyyy HH:mm:ss") : formatDate(value);
}

function ChangeDiffTable({ changes }: { changes: ChangeLogChange[] }) {
	const { t } = useTranslation();
	if (changes.length === 0) return <div className={styles.empty}>{t("changeLogNoDiff")}</div>;
	return (
		<table className={styles.diffTable}>
			<thead>
				<tr>
					<th>{t("changeLogField")}</th>
					<th>{t("changeLogBefore")}</th>
					<th>{t("changeLogAfter")}</th>
				</tr>
			</thead>
			<tbody>
				{changes.map((c, i) => (
					<tr key={`${c.field}-${c.line ?? "h"}-${i}`}>
						<td className={c.line != null ? styles.lineField : undefined}>
							{t(`changeLogFields.${c.field}`, { defaultValue: c.field })}
							{c.line != null && ` (${t("changeLogLine", { n: c.line + 1 })})`}
						</td>
						<td className={styles.diffValue}>{c.before ?? "—"}</td>
						<td className={styles.diffValue}>{c.after ?? "—"}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

interface OrderChangeLogModalProps {
	orderId: string | null;
	documentNumber?: string | number | null | undefined;
	onClose: () => void;
}

export function OrderChangeLogModal({
	orderId,
	documentNumber,
	onClose,
}: OrderChangeLogModalProps) {
	const { t } = useTranslation();
	const query = useOrderChangeLog(orderId);
	const entries = query.data?.entries ?? [];
	const [expanded, setExpanded] = useState<Set<number>>(new Set());
	const toggle = (instance: number) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(instance)) next.delete(instance);
			else next.add(instance);
			return next;
		});

	return (
		<Modal isOpen={!!orderId} onClose={onClose} maxWidth="min(95vw, 1200px)">
			<div className={styles.root}>
				<div className={styles.topBar}>
					<History size={18} />
					<span className={styles.topBarTitle}>
						{t("changeLog")}
						{documentNumber ? ` — ${documentNumber}` : ""}
					</span>
					<button
						type="button"
						className={styles.topBarClose}
						onClick={onClose}
						aria-label={t("close")}
					>
						<X size={18} />
					</button>
				</div>

				<div className={styles.body}>
					{query.isLoading && <LoadingSpinner />}
					{query.isError && <ErrorFallback onRetry={query.refetch} />}
					{!query.isLoading && !query.isError && entries.length === 0 && (
						<div className={styles.empty}>{t("changeLogEmpty")}</div>
					)}
					{!query.isLoading && !query.isError && entries.length > 0 && (
						<div className={styles.tableWrap}>
							<table className={styles.table}>
								<thead>
									<tr>
										<th>{t("orderNumber")}</th>
										<th>{t("changeLogUpdatedByName")}</th>
										<th>{t("changeLogUpdatedAt")}</th>
										<th>{t("changeLogCreatedByName")}</th>
										<th>{t("changeLogCreatedAt")}</th>
										<th />
									</tr>
								</thead>
								<tbody>
									{entries.map((e, index) => (
										<Fragment key={e.instance}>
											<tr>
												<td>{e.docNum}</td>
												<td>
													<CrmUser
														name={e.crmUpdatedBy?.join(", ") ?? null}
														sapName={e.updatedByName}
													/>
												</td>
												<td>{formatSapDateTime(e.updatedAt)}</td>
												<td>
													<CrmUser name={e.crmCreatedBy} sapName={e.createdByName} />
												</td>
												<td>{formatSapDateTime(e.createdAt)}</td>
												<td>
													{index > 0 && (
														<button
															type="button"
															className={styles.diffToggle}
															onClick={() => toggle(e.instance)}
														>
															{expanded.has(e.instance)
																? t("changeLogHideDiff")
																: t("changeLogShowDiff")}
															{e.changes.length > 0 && ` (${e.changes.length})`}
														</button>
													)}
												</td>
											</tr>
											{expanded.has(e.instance) && (
												<tr className={styles.diffRow}>
													<td colSpan={6}>
														<ChangeDiffTable changes={e.changes} />
													</td>
												</tr>
											)}
										</Fragment>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</Modal>
	);
}
